import { AccountTree } from '@mui/icons-material';
import { NotFound } from 'react-admin';
import { Route } from 'react-router-dom';

import { SoftwareComponentCreate } from './SoftwareComponentCreate';
import { SoftwareComponentEdit } from './SoftwareComponentEdit';
import { SoftwareComponentList } from './SoftwareComponentList';
import { SoftwareComponentShow } from './SoftwareComponentShow';
import { SubMenu } from '../../constants/SubMenu';

// One predicate, four gates plus the menu (TVR-2147).
//
// Decision: in this repo `options.isVisible` is *decluttering only*. It is read by
// src/layout/Menu.tsx and hides a sidebar entry; src/components/Resource.tsx never looks at it, so
// on its own it is not an authorization boundary — a user without the permission still reaches
// /software_components, /software_components/create, /software_components/:id and
// /software_components/:id/show by typing or pasting the URL.
//
// The gates that do control routes are `canList` / `canCreate` / `canEdit` / `canShow`, which
// Resource.tsx defaults to `true` for every resource. So the rule for this repo is: where a
// resource is genuinely permission-bound, pair `isVisible` with the four `can*` gates driven by the
// *same* predicate, so the menu and the routes cannot drift apart. `src/resources/roles/index.ts`
// already does exactly this and is the pattern followed here.
//
// Verified against toverzicht-api before choosing: SoftwareComponentSearchRequest,
// SoftwareComponentShowRequest, SoftwareComponentCreateRequest and SoftwareComponentUpdateRequest
// all authorize on `dev_options`, so the API is the real boundary. These gates are a UI
// consistency measure on top of it, not the only control.
const canAccess = (hasPermission: undefined | Function) => !!hasPermission?.('dev_options');

const SoftwareComponentResource = {
  name: 'software_components',
  options: {
    isVisible: (hasPermission: Function) => hasPermission('dev_options'),
    menu: SubMenu.Develop,
    canList: canAccess,
    canShow: canAccess,
    canEdit: canAccess,
    canCreate: canAccess,
  },
  icon: AccountTree,
  list: SoftwareComponentList,
  show: SoftwareComponentShow,
  edit: SoftwareComponentEdit,
  create: SoftwareComponentCreate,
  recordRepresentation: (record: any) => record.name,
  // Without this, a denied route matches react-admin's own `<Route path="software_components/*">`
  // (ra-core CoreAdminRoutes) but nothing inside Resource.tsx's `<Routes>`, so the content area
  // renders `null` — a blank page inside the layout. Resource.tsx renders `props.children` inside
  // that same `<Routes>`, so a splat fallback here gives the same "not found" screen the app
  // already shows for any other unknown URL. The permitted routes outrank it, so it only fires
  // when a gate above has closed.
  children: <Route path="*" element={<NotFound />} />,
};

export default SoftwareComponentResource;
